import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { shopify, backInStockApi } from "@/lib/api";
import { useStore, formatPrice } from "@/lib/store";
import { Heart, Bell, Play } from "lucide-react";
import { toast } from "sonner";
import ColorwaySwitcher from "@/components/ColorwaySwitcher";
import CompleteTheLook from "@/components/CompleteTheLook";
import BundleOffer from "@/components/BundleOffer";
import SizeGuide from "@/components/SizeGuide";
import StickyAddToCart from "@/components/StickyAddToCart";
import { t } from "@/lib/i18n";
import { optimizeImg, srcSet } from "@/lib/img";
import StarRating from "@/components/StarRating";

export default function ProductDetail() {
  const { handle } = useParams();
  const { addToCart, wishlist, toggleWishlist, trackView } = useStore();
  const { data: product } = useQuery({
    queryKey: ["product", handle],
    queryFn: () => shopify.product(handle, "NO"),
    enabled: !!handle,
  });

  const [variantId, setVariantId] = useState(null);
  const [activeMedia, setActiveMedia] = useState(0);
  const [bisEmail, setBisEmail] = useState("");
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);

  useEffect(() => {
    if (product) {
      const firstAvail = product.variants.find(v => v.available) || product.variants[0];
      setVariantId(firstAvail?.id);
      setActiveMedia(0);
      trackView(product.handle);
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [product, trackView]);

  const variant = useMemo(
    () => product?.variants.find(v => v.id === variantId),
    [product, variantId],
  );

  // Unified media list: prefer product.media (images + videos in Shopify order),
  // fall back to legacy images-only array for defensive rendering.
  const mediaList = useMemo(() => {
    if (product?.media?.length) return product.media;
    return (product?.images || []).map((url) => ({ type: "image", url, thumbnail: url }));
  }, [product]);
  const activeItem = mediaList[activeMedia] || mediaList[0];

  if (!product) {
    return <div className="pt-40 px-6 text-center text-[color:var(--marcini-fg-2)]">…</div>;
  }

  const inWishlist = wishlist.includes(product.handle);

  const notifyBis = async (e) => {
    e.preventDefault();
    if (!variant) return;
    try {
      await backInStockApi.subscribe({ email: bisEmail, variant_id: variant.id, product_handle: product.handle });
      toast.success(t.toasts.bisSuccess);
      setBisEmail("");
    } catch {
      toast.error(t.toasts.bisFail);
    }
  };

  return (
    <div className="pt-20 md:pt-28" data-testid={`pdp-${product.handle}`}>
      <div className="grid md:grid-cols-2 gap-0">
        {/* Media column */}
        <div className="relative bg-[color:var(--marcini-bg-warm)] min-h-[70vh] md:min-h-[calc(100vh-6rem)] flex items-center justify-center overflow-hidden" data-testid="pdp-media">
          {activeItem?.type === "image" && (
            <img
              src={optimizeImg(activeItem.url, 2000, { hq: true })}
              srcSet={srcSet(activeItem.url, [1000, 1400, 2000, 2400], { hq: true })}
              sizes="(min-width: 768px) 50vw, 100vw"
              alt={product.title}
              className="w-full h-full object-cover"
            />
          )}
          {activeItem?.type === "video" && (
            <video
              key={activeItem.url}
              src={activeItem.url}
              poster={activeItem.thumbnail || undefined}
              autoPlay
              muted
              loop
              playsInline
              controls
              className="w-full h-full object-cover"
              data-testid="pdp-video"
            />
          )}
          {activeItem?.type === "external_video" && (
            <iframe
              key={activeItem.url}
              src={activeItem.url}
              title={product.title}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              data-testid="pdp-external-video"
            />
          )}

          {/* Thumbnails on the side (desktop) / bottom (mobile) */}
          {mediaList.length > 1 && (
            <div className="absolute left-3 bottom-3 md:left-4 md:top-4 md:bottom-auto flex md:flex-col gap-2 overflow-x-auto no-scrollbar max-w-[calc(100%-24px)]" data-testid="pdp-thumbs">
              {mediaList.map((m, i) => (
                <button
                  key={(m.thumbnail || m.url) + i}
                  onClick={() => setActiveMedia(i)}
                  className={`relative w-12 h-16 md:w-14 md:h-20 overflow-hidden border transition-colors flex-shrink-0 ${
                    activeMedia === i ? "border-black" : "border-white/60 hover:border-black"
                  }`}
                  aria-label={`Vis media ${i + 1}`}
                >
                  <img src={optimizeImg(m.thumbnail || m.url, 160)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  {m.type !== "image" && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/15">
                      <div className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center">
                        <Play size={10} className="text-black translate-x-[1px]" />
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details column */}
        <div className="px-5 md:px-14 py-10 md:py-20 max-w-2xl">
          {product.subtitle && (
            <p className="text-eyebrow text-[color:var(--marcini-fg-3)]" data-testid="pdp-subtitle">{product.subtitle}</p>
          )}
          <h1 className="font-serif-editorial text-4xl md:text-6xl mt-2 mb-4 md:mb-6 leading-[0.98]">{product.title}</h1>
          <div className="flex items-center gap-4 flex-wrap">
            <p className="text-lg" data-testid="pdp-price">{formatPrice(variant?.price || product.price_range.min)}</p>
            <StarRating handle={product.handle} size={14} showCount />
          </div>

          {product.description && (
            <div className="mt-6 md:mt-8 text-[color:var(--marcini-fg-2)] leading-relaxed space-y-3" data-testid="pdp-description">
              {product.description
                .replace(/\.([A-ZÅÆØ])/g, '. $1')
                .split(/(?<=[.!?])\s+(?=[A-ZÅÆØ])/)
                .filter((s) => s.trim().length > 0)
                .map((sentence, i) => (
                  <p key={i} className="whitespace-pre-line">{sentence.trim()}</p>
                ))}
            </div>
          )}

          {/* Colorway switcher */}
          <ColorwaySwitcher product={product} />

          {/* Variant selector */}
          {product.options?.[0]?.values?.length > 1 && (
            <div className="mt-8 md:mt-10">
              <div className="flex items-center justify-between mb-3">
                <p className="text-eyebrow">{t.pdp.size}</p>
                <button
                  onClick={() => setSizeGuideOpen(true)}
                  className="text-eyebrow text-[color:var(--marcini-fg-3)] link-underline"
                  data-testid="pdp-size-guide-open"
                >{t.pdp.sizeGuide}</button>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="pdp-variants">
                {product.variants.map(v => {
                  const selected = v.id === variantId;
                  return (
                    <button
                      key={v.id}
                      data-testid={`variant-${v.title}`}
                      disabled={!v.available}
                      onClick={() => setVariantId(v.id)}
                      className={`px-4 py-3 md:py-3 border text-sm min-w-[3.5rem] min-h-[44px] transition-colors ${
                        selected ? "border-black bg-black text-white" : "border-[color:var(--marcini-border)]"
                      } ${!v.available ? "text-[color:var(--marcini-fg-3)] line-through cursor-not-allowed" : "hover:border-black"}`}
                    >{v.title}</button>
                  );
                })}
              </div>
            </div>
          )}

          {/* CTAs */}
          <div className="mt-8 md:mt-10 flex flex-col gap-3">
            {variant?.available ? (
              <button
                data-testid="pdp-add-to-cart"
                onClick={() => addToCart(variant.id, 1)}
                className="w-full bg-black text-white py-4 text-eyebrow hover:bg-black/85 transition-colors"
              >{t.pdp.addToBag} · {formatPrice(variant.price)}</button>
            ) : (
              <div className="border border-[color:var(--marcini-border)] p-5 text-center" data-testid="pdp-sold-out-info">
                <p className="text-eyebrow flex items-center justify-center gap-2 text-[color:var(--marcini-fg-3)]">
                  <Bell size={14} /> {t.common.soldOut}
                </p>
              </div>
            )}
            <button
              data-testid="pdp-wishlist"
              onClick={() => toggleWishlist(product.handle)}
              className="w-full border border-black py-4 text-eyebrow inline-flex items-center justify-center gap-2 hover:bg-black hover:text-white transition-colors"
            >
              <Heart size={14} className={inWishlist ? "fill-current" : ""} /> {inWishlist ? t.pdp.savedToWishlist : t.pdp.saveToWishlist}
            </button>
          </div>

          <BundleOffer product={product} currentVariantId={variantId} />

          {/* Meta */}
          <div className="mt-12 md:mt-14 divide-y divide-[color:var(--marcini-border)] border-y border-[color:var(--marcini-border)]">
            {product.material && <Row label={t.pdp.material} value={product.material} />}
            {product.care && <Row label={t.pdp.care} value={product.care} />}
            <Row label={t.pdp.shipping} value={t.pdp.shippingBody} />
            <Row label={t.pdp.returns} value={t.pdp.returnsBody} />
          </div>
        </div>
      </div>

      <CompleteTheLook handle={product.handle} />

      <SizeGuide open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)} />
      <StickyAddToCart
        product={product}
        variant={variant}
        onSelectSize={() => {
          const el = document.querySelector('[data-testid="pdp-variants"]');
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }}
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <details className="group py-4 px-0">
      <summary className="flex items-center justify-between cursor-pointer list-none">
        <span className="text-eyebrow">{label}</span>
        <span className="text-[color:var(--marcini-fg-3)] group-open:rotate-45 transition-transform">+</span>
      </summary>
      <p className="mt-3 text-sm text-[color:var(--marcini-fg-2)] whitespace-pre-line">{value}</p>
    </details>
  );
}
